import { useState } from 'react';

import { Braces, CalendarDays, ChevronDown, Clock, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useCreateBooking, useUpdateBooking } from '@/hooks/useBookings';

import type { BookingWithRelations } from '@repo/shared-types';

interface BookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking?: BookingWithRelations | null;
}

const parseBookingDateTime = (booking: BookingWithRelations) => {
  const d = new Date(booking.date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const date = `${year}-${month}-${day}`;
  const hasTime = (booking as BookingWithRelations & { has_time?: boolean }).has_time !== false;
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const time = hasTime ? `${hours}:${minutes}` : '';
  return { date, time };
};

interface BookingFormProps {
  booking?: BookingWithRelations | null;
  onOpenChange: (open: boolean) => void;
}

const BookingForm = ({ booking, onOpenChange }: BookingFormProps) => {
  const { toast } = useToast();
  const createBooking = useCreateBooking();
  const updateBooking = useUpdateBooking();

  const isEdit = !!booking;
  const initial = booking ? parseBookingDateTime(booking) : { date: '', time: '' };

  const hadData = !!booking?.data && Object.keys(booking.data).length > 0;

  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [title, setTitle] = useState(booking?.title ?? '');
  const [description, setDescription] = useState(booking?.description ?? '');
  const [dataText, setDataText] = useState(
    booking?.data ? JSON.stringify(booking.data, null, 2) : '',
  );
  const [dataError, setDataError] = useState<string | null>(null);
  const [dataOpen, setDataOpen] = useState(hadData);

  const handleSubmit = async () => {
    if (!date || !title) {
      toast({ title: 'Date and title are required', variant: 'destructive' });
      return;
    }

    const trimmedData = dataText.trim();
    let dataPayload: Record<string, unknown> | null | undefined;
    if (!trimmedData) {
      // Only send an explicit null when the booking previously had data; otherwise omit.
      dataPayload = booking?.data ? null : undefined;
    } else {
      try {
        const parsed: unknown = JSON.parse(trimmedData);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          setDataError('Data must be a JSON object');
          setDataOpen(true);
          return;
        }
        dataPayload = parsed as Record<string, unknown>;
      } catch {
        setDataError('Invalid JSON');
        setDataOpen(true);
        return;
      }
    }
    setDataError(null);

    const hasTime = time !== '';
    const dateTime = `${date}T${hasTime ? time : '00:00'}:00`;

    try {
      if (isEdit) {
        await updateBooking.mutateAsync({
          id: booking.id,
          data: {
            date: dateTime,
            title,
            description: description || null,
            has_time: hasTime,
            ...(dataPayload !== undefined && { data: dataPayload }),
          },
        });
        toast({ title: 'Booking updated' });
      } else {
        await createBooking.mutateAsync({
          date: dateTime,
          title,
          description: description || null,
          has_time: hasTime,
          ...(dataPayload !== undefined && { data: dataPayload }),
        });
        toast({ title: 'Booking created' });
      }
      onOpenChange(false);
    } catch {
      toast({
        title: `Failed to ${isEdit ? 'update' : 'create'} booking`,
        variant: 'destructive',
      });
    }
  };

  const saving = createBooking.isPending || updateBooking.isPending;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-base">{isEdit ? 'Edit Booking' : 'New Booking'}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-3">
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-muted-foreground mb-1.5 flex items-center gap-1.5 text-xs font-medium">
              <CalendarDays className="h-3 w-3" />
              Date *
            </label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-9 text-sm"
            />
          </div>
          <div className="w-36">
            <label className="text-muted-foreground mb-1.5 flex items-center gap-1.5 text-xs font-medium">
              <Clock className="h-3 w-3" />
              Time (optional)
            </label>
            <div className="relative">
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="h-9 pr-7 text-sm"
              />
              {time && (
                <button
                  type="button"
                  onClick={() => setTime('')}
                  className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1.5 -translate-y-1/2"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
        <div>
          <label className="text-muted-foreground mb-1.5 block text-xs font-medium">Title *</label>
          <Input
            placeholder="e.g. Shift confirmation, Interview..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={255}
            className="h-9 text-sm"
          />
        </div>
        <div>
          <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
            Description
          </label>
          <textarea
            className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[72px] w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
            placeholder="Add notes or details..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <Collapsible open={dataOpen} onOpenChange={setDataOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-xs font-medium"
            >
              <Braces className="h-3 w-3" />
              Data (JSON)
              <ChevronDown
                className={`h-3 w-3 transition-transform ${dataOpen ? 'rotate-180' : ''}`}
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <textarea
              className={`border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring mt-1.5 flex min-h-[96px] w-full rounded-md border px-3 py-2 font-mono text-xs focus-visible:ring-2 focus-visible:outline-none ${
                dataError ? 'border-destructive' : ''
              }`}
              placeholder='{ "vehicle": { "registration": "AB12 CDE" } }'
              value={dataText}
              onChange={(e) => {
                setDataText(e.target.value);
                if (dataError) setDataError(null);
              }}
              spellCheck={false}
            />
            {dataError && <p className="text-destructive mt-1 text-xs">{dataError}</p>}
          </CollapsibleContent>
        </Collapsible>
      </div>
      <DialogFooter>
        <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Booking'}
        </Button>
      </DialogFooter>
    </>
  );
};

export const BookingDialog = ({ open, onOpenChange, booking }: BookingDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        {open && (
          <BookingForm key={booking?.id ?? 'new'} booking={booking} onOpenChange={onOpenChange} />
        )}
      </DialogContent>
    </Dialog>
  );
};
